import type { ComponentType } from 'react';
import { Button } from '../ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip';
import { cn } from '../../lib/utils';

interface ToolbarIconButtonProps extends Omit<React.ComponentProps<typeof Button>, 'size'> {
  icon: ComponentType<{ className?: string }>;
  label: string;
  /** Tooltip placement. Defaults to bottom for horizontal toolbars. */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Compact icon-only button (`size-7`) with a tooltip.
 * Use inside `PanelHeader`, `SidebarHeader`, toolbar rows, or anywhere a
 * dense icon action is needed. Pairs with `Button size="toolbar"` for
 * icon+text actions in the same row.
 *
 * Defaults to `text-muted-foreground` so toolbar icons render at a
 * consistent subdued brightness. Pass a different text-color class in
 * `className` to override (e.g. `text-destructive`).
 */
export function ToolbarIconButton({
  icon: Icon,
  label,
  variant = 'ghost',
  className,
  tooltipSide = 'bottom',
  ...props
}: ToolbarIconButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant={variant}
          size="toolbar-icon"
          aria-label={label}
          className={cn('text-muted-foreground', className)}
          {...props}
        >
          <Icon className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{label}</TooltipContent>
    </Tooltip>
  );
}
